import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TablePageEvent } from 'primeng/table';
import { lastValueFrom } from 'rxjs';
import { ItOpsControllerService } from 'src/app/admin-api/api/itOpsController.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-integrity-query-list',
  templateUrl: './integrity-query-list.component.html',
  styleUrls: ['./integrity-query-list.component.scss'],
  providers: [ConfirmationService, MessageService]
})
export class IntegrityQueryListComponent implements OnInit {
  integrityId!: number;
  integrityName = '';
  resolutionType = '';
  columns: string[] = [];
  rows: any[] = [];
  selectedRows: any[] = [];
  readonly rowIdKey = '__rowId';
  totalRecords = 0;
  rowsPerPage = 25;
  readonly rowsPerPageOptions = [25, 50, 100, 1000];

  loading = true;
  executing = false;
  resolving = false;
  errorMessage = '';

  get isStandalone(): boolean {
    return this.normalizeResolutionType(this.resolutionType) === 'JAVA_HANDLER_STANDALONE';
  }

  showEditDialog = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private itOpsService: ItOpsControllerService,
    private confirm: ConfirmationService,
    private message: MessageService
  ) {}

  async ngOnInit() {
    this.integrityId = Number(this.route.snapshot.paramMap.get('id'));

    try {
      const res: any = await lastValueFrom(
        this.itOpsService.getIntegrity(this.integrityId)
      );
      const name = res?.integrityName ?? res?.result?.integrityName ?? '';
      if (name) {
        this.integrityName = name;
      }
      this.resolutionType = this.normalizeResolutionType(
        res?.resolutionType ?? res?.result?.resolutionType ?? ''
      );
    } catch (error) {
      /* keep screen functional */
    }

    if (!this.isStandalone) {
      this.load();
    } else {
      this.loading = false;
    }
  }

  private normalizeResolutionType(value: any): string {
    return String(value ?? '')
      .trim()
      .toUpperCase();
  }

  private parsePayload(res: any): { integrityName?: string; result: any[] } {
    if (res?.result && !Array.isArray(res.result)) {
      return {
        integrityName: res.result.integrityName,
        result: res.result.result ?? []
      };
    }
    if (res?.integrityName || Array.isArray(res?.result)) {
      return { integrityName: res.integrityName, result: res.result ?? [] };
    }
    if (Array.isArray(res)) {
      return { result: res };
    }
    return { result: [] };
  }

  async load() {
    this.loading = true;
    this.errorMessage = '';
    try {
      LoaderService.showLoader();
      const res: any = await lastValueFrom(
        this.itOpsService.executeIntegrity(this.integrityId)
      );
      const payload = this.parsePayload(res);
      this.integrityName = payload.integrityName ?? this.integrityName ?? '';
      const prepared = this.prepareRows(payload.result ?? []);
      this.rows = prepared.rows;
      this.columns = prepared.columns;
      this.selectedRows = [];
      this.totalRecords = this.rows.length;
    } catch (err) {
      console.error('Erro ao executar integridade:', err);
      if (!this.isStandalone) {
        this.errorMessage = 'Falha ao carregar os dados da integridade.';
        AppDialogService.showErrorDialog(err);
      }
    } finally {
      this.loading = false;
      LoaderService.showLoader(false);
    }
  }

  private hardReload(): void {
    this.executing = true;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/it-ops/integrity', this.integrityId]);
    });
  }

  async refresh() {
    this.executing = true;
    this.errorMessage = '';
    try {
      LoaderService.showLoader();
      await lastValueFrom(this.itOpsService.refreshIntegrity(this.integrityId));
      this.hardReload();
    } catch (error) {
      this.hardReload();
    } finally {
      LoaderService.showLoader(false);
    }
  }

  resolve(): void {
    if (this.resolving) return;

    const selectedCount = this.selectedRows.length;
    const hasSelection = selectedCount > 0;

    this.confirm.confirm({
      header: 'Resolver Problemas',
      message: hasSelection
        ? `Deseja executar a rotina apenas para ${selectedCount} registro(s) selecionado(s)?`
        : 'Deseja executar a rotina de correção para todos os registros desta integridade? Esta ação não pode ser desfeita.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Resolver',
      rejectLabel: 'Cancelar',
      accept: async () => {
        this.resolving = true;
        LoaderService.showLoader();
        const payload = hasSelection
          ? this.stripMetaFromRows(this.selectedRows)
          : undefined;

        try {
          const res: any = await lastValueFrom(
            this.itOpsService.resolveIntegrity(this.integrityId, payload)
          );
          const resPayload = res?.result ?? res ?? {};
          const success = resPayload?.success !== false;
          const detail =
            resPayload?.message ??
            (success
              ? 'Rotina de correção executada com sucesso.'
              : 'Rotina executada com advertências.');
          this.message.add({
            severity: success ? 'success' : 'warn',
            summary: success ? 'Sucesso' : 'Aviso',
            detail
          });
          this.selectedRows = [];
          if (this.isStandalone) {
            this.resolving = false;
          } else {
            this.refreshAfterResolve();
          }
        } catch (err) {
          console.error('Erro ao resolver integridade:', err);
          AppDialogService.showErrorDialog(err);
          this.resolving = false;
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  private async refreshAfterResolve() {
    this.resolving = true;
    try {
      LoaderService.showLoader();
      await lastValueFrom(this.itOpsService.refreshIntegrity(this.integrityId));
      this.message.add({
        severity: 'success',
        summary: 'Resolvido',
        detail: 'Rotina executada e contador atualizado.'
      });
      this.resolving = false;
      this.load();
    } catch (err) {
      console.error('Erro ao atualizar contador após resolução', err);
      this.message.add({
        severity: 'warn',
        summary: 'Resolvido com aviso',
        detail:
          'Rotina executada, mas não foi possível atualizar o contador automaticamente.'
      });
      this.resolving = false;
      this.load();
    } finally {
      LoaderService.showLoader(false);
    }
  }

  onPage(event: TablePageEvent): void {
    if (event.rows) {
      this.rowsPerPage = event.rows;
    }
  }

  openEdit(): void {
    this.showEditDialog = true;
  }

  onDialogHide(): void {
    /* keep state */
  }

  onFormSaved(): void {
    this.showEditDialog = false;
    this.hardReload();
  }

  onFormDeleted(): void {
    this.showEditDialog = false;
    this.router.navigate(['/it-ops/integrity']);
  }

  delete(id?: number, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    const targetId = id ?? this.integrityId;

    this.confirm.confirm({
      header: 'Excluir integridade',
      message: 'Tem certeza que deseja excluir esta integridade?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      accept: async () => {
        this.executing = true;
        LoaderService.showLoader();
        try {
          await lastValueFrom(this.itOpsService.deleteIntegrity(targetId));
          this.executing = false;
          this.router.navigate(['/it-ops/integrity']);
        } catch (err) {
          console.error('Erro ao excluir integridade:', err);
          this.errorMessage = 'Falha ao excluir a integridade.';
          AppDialogService.showErrorDialog(err);
          this.executing = false;
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  private prepareRows(data: any[]): { rows: any[]; columns: string[] } {
    const batchId = Date.now();
    const rows = (Array.isArray(data) ? data : []).map((row, index) =>
      this.enrichRow(row, index, batchId)
    );
    const columns =
      rows.length > 0
        ? Object.keys(rows[0]).filter((key) => key !== this.rowIdKey)
        : [];
    return { rows, columns };
  }

  private enrichRow(row: any, index: number, batchId: number): any {
    const base =
      row && typeof row === 'object' && !Array.isArray(row)
        ? { ...row }
        : { value: row };
    if (!base[this.rowIdKey]) {
      base[this.rowIdKey] = `${batchId}-${index}`;
    }
    return base;
  }

  private stripMetaFromRows(rows: any[]): any[] {
    return rows.map((row) => {
      if (row && typeof row === 'object') {
        const rest = { ...row };
        delete rest[this.rowIdKey];
        return rest;
      }
      return row;
    });
  }

  get resolveButtonLabel(): string {
    return this.selectedRows.length ? 'Resolver selecionados' : 'Resolver tudo';
  }

  async copyColumn(column: string, ev?: Event): Promise<void> {
    ev?.stopPropagation();
    if (!column) return;
    const lines = this.rows.map((row) => this.formatValue(row[column]));
    const text = lines.join('\n');
    const ok = await this.writeToClipboard(text);
    this.message.add({
      severity: ok ? 'success' : 'warn',
      summary: ok ? 'Coluna copiada' : 'Falha ao copiar',
      detail: ok
        ? `Valores da coluna "${column}" enviados para a área de transferência.`
        : 'Não foi possível acessar a área de transferência.'
    });
  }

  async copyRow(row: any, ev?: Event): Promise<void> {
    ev?.stopPropagation();
    if (!row) return;
    const [pureRow] = this.stripMetaFromRows([row]);
    const values = this.columns.map((col) => this.formatValue(pureRow?.[col]));
    const text = values.join('\t');
    const ok = await this.writeToClipboard(text);
    this.message.add({
      severity: ok ? 'success' : 'warn',
      summary: ok ? 'Linha copiada' : 'Falha ao copiar',
      detail: ok
        ? 'Conteúdo da linha enviado para a área de transferência.'
        : 'Não foi possível acessar a área de transferência.'
    });
  }

  private formatValue(value: any): string {
    if (value == null) return '';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  private async writeToClipboard(text: string): Promise<boolean> {
    if (!text) return false;
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      console.warn('navigator.clipboard.writeText falhou', err);
    }

    try {
      if (typeof document === 'undefined') {
        return false;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    } catch (err) {
      console.warn('Fallback clipboard copy falhou', err);
      return false;
    }
  }
}